/**
 * Measures a view's laid-out height (SE-8526): the Pitch column reports how
 * tall its visible area is, so the field can scale to fit inside it.
 * @module features/scouting/hooks/useMeasuredHeight
 */
import { useCallback, useState } from "react";
import type { LayoutChangeEvent } from "react-native";

/** The measured height (null until the first layout) and its handler. */
export interface MeasuredHeight {
  readonly height: number | null;
  readonly onLayout: (event: LayoutChangeEvent) => void;
}

/**
 * Tracks the height of the view that receives `onLayout`.
 * @returns The latest height and the layout handler.
 */
export const useMeasuredHeight = (): MeasuredHeight => {
  const [height, setHeight] = useState<number | null>(null);
  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const next = event.nativeEvent.layout.height;
    if (next > 0) setHeight(previous => (previous === next ? previous : next));
  }, []);
  return { height, onLayout };
};
